import { Dispatch, SetStateAction, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { find } from 'lodash';
import sortBy from 'lodash/sortBy';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import InvestAssetDeleteButton from '@components/Group/Portfolio/InvestAssetDeleteButton';

import {
  CapitalChangeApiFormInputs,
  InvestAssetAPIResponseTypes,
  InvestAssetApiRequestInputs,
  InvestInfosTypes,
  PostManagementFormInputs,
  PostManagementUpdatePayload,
  StockholderApiInputs,
} from '@type/portfolio';

import { useInternalRouter } from '@hooks/useInternalRouter';
import useQueryString from '@hooks/useQueryString';

import { palette } from '@styles/theme';

import {
  TRANSACTION_TYPES,
  STOCK_TYPES,
  POST_MANAGEMENT_TYPES,
  PRESS_TYPES,
} from '@constants/commons';
import {
  CREATE_ERROR_MESSAGE,
  CREATE_SUCCESS_MESSAGE,
  DELETE_ERROR_MESSAGE,
  DELETE_SUCCESS_MESSAGE,
  UPDATE_ERROR_MESSAGE,
  UPDATE_SUCCESS_MESSAGE,
} from '@constants/toastMessage';

import { comma, isNotNil } from '@utils/utils';

import {
  create,
  createThrowError,
  createWithFormData,
  getList,
  getOne,
  remove,
  update,
} from '@apis/api';

/** 조합 관리 > 상세 > 포트폴리오사 목록 */
export function usePortfolioList() {
  const { groupId } = useParams();
  const { data, isLoading } = useQuery(
    ['portfolio-list', groupId],
    () => getList(`/fund/${groupId}/portfolio?page=1&size=100`, ''),
    {
      enabled: groupId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data?.content || [],
    totalCount: data?.totalCount || 0,
    isLoading,
  };
}

/** 포트폴리오 상세 > 기본정보 */
export function usePortfolioDetail() {
  const { groupId, portfolioId } = useParams();

  const { data, isLoading } = useQuery(
    ['portfolio-detail', groupId, portfolioId],
    () => getOne(`/fund/${groupId}/portfolio`, portfolioId),
    {
      suspense: true,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 포트폴리오 상세 > 월간 성장 비교 데이터 */
export function useMonthlyGrowth(mappingId: string, investDate?: string) {
  const url = investDate
    ? `/data-ref/monthly-growth?mappingId=${mappingId}&investDate=${investDate}`
    : `/data-ref/monthly-growth?mappingId=${mappingId}`;

  const isUnder25 = Number(dayjs().format('D')) < 25 ? 1 : 0;

  const { data, isLoading } = useQuery(
    ['monthly-growth', mappingId, investDate],
    () => getList(url, ''),
    {
      enabled: investDate
        ? dayjs()
            .subtract(1 + isUnder25, 'month')
            .diff(dayjs(investDate), 'day') > 0
        : true,
      suspense: true,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 포트폴리오사 - 성장정보: 트래픽 */
export function useTrafficPerMonth(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['growth-traffic'],
    () => getList(`/data-ref/mau?mappingId=${mappingId}`, ''),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data?.data || [],
    isLoading,
  };
}

/** 포트폴리오사 - 성장정보: 소비자거래액 */
export function useSalesPerMonth(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['growth-sales'],
    () => getList(`/data-ref/sales?mappingId=${mappingId}`, ''),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data || [],
    isLoading,
  };
}

/** 포트폴리오사 - 성장정보: 고용인원 */
export function useEmployeePerMonth(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['growth-employee'],
    () => getList(`/data-ref/employee?mappingId=${mappingId}`, ''),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data?.[0]?.list || [],
    isLoading,
  };
}

/** 포트폴리오사 - 투자유치이력 */
export function useInvestHistory(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['invest-history', mappingId],
    () => getList(`/data-ref/invest?mappingId=${mappingId}`, ''),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: sortBy(data?.content, 'date').reverse(),
    isLoading,
  };
}

/** 포트폴리오 상세조회 - 자본금 변동내역 목록 조회 */
export function usePortfolioCapitalChangeList() {
  const { groupId, portfolioId } = useParams();
  const { pageNo, pageSize } = useQueryString();
  const { data, isLoading } = useQuery(
    ['capital-change-list', groupId, portfolioId, pageNo],
    () =>
      getList(
        `/fund/${groupId}/portfolio/${portfolioId}/capital-change?page=${pageNo}&size=${pageSize}`,
        ''
      ),
    {
      suspense: true,
    }
  );

  return {
    data: data?.content,
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 자본금 변동내역 - 등록하기 */
export function useCapitalChangeCreate(
  onSuccess: () => void,
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const { groupId, portfolioId } = useParams();
  const { pageNo } = useQueryString();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ['capital-change-create', groupId, portfolioId],
    (value: CapitalChangeApiFormInputs) =>
      create(`/fund/${groupId}/portfolio/${portfolioId}/capital-change`, value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'capital-change-list',
            groupId,
            portfolioId,
            pageNo,
          ]);
          onSuccess();
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 자본금 변동내역 - 삭제하기 */
export function useCapitalChangeDelete(
  capitalChangeId: string,
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const { groupId, portfolioId } = useParams();
  const { pageNo } = useQueryString();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ['capital-change-delete', groupId, portfolioId, capitalChangeId],
    () =>
      remove(
        `/fund/${groupId}/portfolio/${portfolioId}/capital-change/${capitalChangeId}`
      ),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);

          queryClient.invalidateQueries([
            'capital-change-list',
            groupId,
            portfolioId,
            pageNo,
          ]);
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 자본금 변동내역 - 단건 조회 */
export function useCapitalChangeDetail(capitalChangeId: string | null) {
  const { groupId, portfolioId } = useParams();
  const { data, isLoading } = useQuery(
    ['capital-change-detail', groupId, portfolioId, capitalChangeId],
    () =>
      getOne(
        `/fund/${groupId}/portfolio/${portfolioId}/capital-change`,
        capitalChangeId ?? ''
      ),
    {
      enabled: !!capitalChangeId,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 자본금 변동내역 - 수정하기 */
export function useCapitalChangeUpdate(
  capitalChangeId: string,
  onSuccess: () => void
) {
  const { groupId, portfolioId } = useParams();
  const { pageNo } = useQueryString();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ['capital-change-update', groupId, portfolioId, capitalChangeId],
    (value: CapitalChangeApiFormInputs) =>
      update(
        `/fund/${groupId}/portfolio/${portfolioId}/capital-change/${capitalChangeId}`,
        value
      ),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(UPDATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
        } else {
          toast.success(UPDATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'capital-change-list',
            groupId,
            portfolioId,
            pageNo,
          ]);
          queryClient.invalidateQueries([
            'capital-change-detail',
            groupId,
            portfolioId,
            capitalChangeId,
          ]);
          onSuccess();
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오사 - 주요거래처 */
export function useCustomerList(mappingId: string) {
  const { pageNo, pageSize } = useQueryString();
  const { data, isLoading } = useQuery(
    ['customer-list'],
    () =>
      getList(
        `/data-ref/customer?mappingId=${mappingId}&page=${pageNo}&size=${pageSize}`,
        ''
      ),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data?.content,
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 포트폴리오사 - 손익재무 */
export function useFinance(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['finance'],
    () => getList(`/data-ref/finance?mappingId=${mappingId}`, ''),
    {
      enabled: mappingId !== undefined,
      suspense: true,
    }
  );

  return {
    data: data?.data || [],
    isLoading,
  };
}

/** 포트폴리오 상세 > 주주 현황 > 목록 조회 */
export function useStockhloderList() {
  const { groupId, portfolioId } = useParams();
  const { data, isLoading } = useQuery(
    ['stockholder-list', groupId, portfolioId],
    () =>
      getList(
        `/fund/${groupId}/portfolio/${portfolioId}/stockholder/status`,
        ''
      ),
    {
      suspense: true,
    }
  );

  const tableData = useMemo(
    () =>
      data?.infos?.map((d: any, i: number) => ({
        id: d?.id,
        no: i + 1,
        name: d?.name || '-',
        shareholding: d?.shareholding ? comma(d.shareholding) : '-',
        shareholdingSort: d?.shareholding ? d?.shareholding : 0,
        shareholdingRatio:
          d.shareholdingRatio != null && d.shareholdingRatio !== ''
            ? Number(d.shareholdingRatio).toFixed(2) + '%'
            : '-',
        shareholdingRatioSort:
          d.shareholdingRatio != null && d.shareholdingRatio !== ''
            ? Number(d.shareholdingRatio)
            : 0,
        stockType: find(STOCK_TYPES, { value: d.type })?.label,
      })),
    [data]
  );

  return {
    data,
    tableData,
    isLoading,
  };
}

/** 포트폴리오 상세 > 주주 현황 > 히스토리 관리 > 불러오기 > 등록/수정 */
export function useStockholerHistoryById() {
  const { groupId, portfolioId, stockholderId } = useParams();
  const { data, isLoading } = useQuery(
    ['stockholder-history-detail', groupId, portfolioId, stockholderId],
    () =>
      getOne(
        `/fund/${groupId}/portfolio/${portfolioId}/stockholder/history`,
        stockholderId
      ),
    {
      suspense: true,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 포트폴리오 상세 > 주주 현황 > 등록 */
export function useStockholderCreate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const queryClient = useQueryClient();
  const { groupId, portfolioId } = useParams();
  const { mutate, isLoading } = useMutation(
    (data: FormData | StockholderApiInputs) => {
      // FormData인지 확인
      if (data instanceof FormData) {
        return createWithFormData(
          `/fund/${groupId}/portfolio/${portfolioId}/stockholder`,
          data
        );
      } else {
        // 기존 JSON 방식
        return create(
          `/fund/${groupId}/portfolio/${portfolioId}/stockholder`,
          data
        );
      }
    },

    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);

          queryClient.invalidateQueries([
            'stockholder-list',
            groupId,
            portfolioId,
          ]);
          router.replace(
            `/group/${groupId}/portfolio/${portfolioId}/stockholder`
          );
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 주주 현황 > 히스토리 조회 */
export function useStockholderHistory(pageNo: number, pageSize: number) {
  const { groupId, portfolioId } = useParams();
  const { data, isLoading } = useQuery(
    ['stockholder-history', groupId, portfolioId, pageNo],
    () =>
      getList(`/fund/${groupId}/portfolio/${portfolioId}/stockholder/history`, {
        page: pageNo,
        size: pageSize,
        sort: 'createdAt,desc',
      }),
    {
      suspense: true,
    }
  );

  return {
    data: data?.content || [],
    totalCount: data?.totalCount || 0,
    isLoading,
  };
}

/** 혁신의숲 기업 조회 (검색어 조회) */
export function useInnoforestCompany() {
  const { mutate, data, isLoading } = useMutation(
    ['innoforest-company-list'],
    (value: { name: string; page: number; size: number }) =>
      getList(`/data-ref/corporation`, value),
    {
      onSuccess: (res: any) => {
        console.log('COMPANY SEARCH : ', res);
      },
    }
  );

  return {
    mutate,
    data: data?.content,
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 포트폴리오 등록 */
export function usePortfolioCreate(
  assigneeList: { email: string; name?: string; phone?: string }[],
  investList: InvestInfosTypes[],
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId } = useParams();
  const { mutate, isLoading } = useMutation(
    (value: { mappingId: string }) =>
      create(`/fund/${groupId}/portfolio`, value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE + ' - ' + res?.data?.message);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          const investInfos = investList?.map((d: any) => ({
            ...d,
            faceValue: d.faceValue?.replaceAll(',', ''),
            totalShares: d.totalShares?.replaceAll(',', ''),
            investmentUnitPrice: d.investmentUnitPrice?.replaceAll
              ? d.investmentUnitPrice?.replaceAll(',', '')
              : undefined,
            shareholding: d?.shareholding
              ? d.shareholding?.replaceAll(',', '')
              : undefined,
            investmentAmount: d?.investmentAmount?.replaceAll(',', ''),
            corporateValue: d?.corporateValue?.replaceAll(',', ''),
            stockType: d?.stockType?.value,
          }));

          portfolioCreate({
            groupId: String(groupId),
            portfolioId: res?.id,
            assigneeList,
            investList: investInfos,
          });

          setConfirmModalOpen(false);
          router.replace(`/group/${groupId}`);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 삭제 */
export function usePortfolioDelete(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId, portfolioId } = useParams();

  const { mutate, isLoading } = useMutation(
    () => remove(`/fund/${groupId}/portfolio/${portfolioId}`),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);

          router.replace(`/group/${groupId}`);
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 수정 */
export function usePortfolipUpdate(
  // refetch: any,
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId, portfolioId } = useParams();

  const { mutate, isLoading } = useMutation(
    async (value: any) => await portfolipUpdate(value),
    {
      onSuccess: (res: any) => {
        setConfirmModalOpen(false);

        if (res?.isError && !res?.hasPartialSuccess) {
          // 전체 실패 - 각 실패 항목별로 Toast 표시
          res?.errors?.forEach((error: { name: string; message: string }) => {
            toast.error(`${error.name} 실패: ${error.message}`, {
              autoClose: 5000,
            });
          });
        } else if (res?.hasPartialSuccess) {
          // 일부 성공, 일부 실패
          // 성공 메시지
          if (res?.successes?.length > 0) {
            toast.success(`${res?.fulfilledCount}개 항목 저장완료`, {
              autoClose: 3000,
            });
          }
          // 실패 메시지 - 각각 표시
          res?.errors?.forEach((error: { name: string; message: string }) => {
            toast.error(`${error.name} 저장 실패: ${error.message}`, {
              autoClose: 5000,
            });
          });
          setTimeout(() => {
            // refetch();
            router.replace(`/group/${groupId}/portfolio/${portfolioId}`);
          }, 1000);
        } else {
          // 전체 성공
          setTimeout(() => {
            // refetch();
            router.replace(`/group/${groupId}/portfolio/${portfolioId}`);
            toast.success(UPDATE_SUCCESS_MESSAGE);
          }, 1000);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 담당자 추가 */
export const assigneeCreate = async (
  groupId: string,
  portfolioId: string,
  assigneeList: { email: string; name?: string; phone?: string }[]
) => {
  return await assigneeList.reduce(async (promise, list) => {
    await promise;
    await createThrowError(
      `/fund/${groupId}/portfolio/${portfolioId}/assignee`,
      list
    );
  }, Promise.resolve());
};

/** 포트폴리오 담당자 수정 */
export const assingneeUpdate = async (
  groupId: string,
  portfolioId: string,
  assigneeList: {
    idx?: number;
    email: string;
    name?: string;
    phone?: string;
  }[]
) => {
  return await assigneeList.reduce(async (promise, list) => {
    await promise;
    const result = await update(
      `/fund/${groupId}/portfolio/${portfolioId}/assignee/${list?.idx}`,
      list
    );
    if (result?.isError) {
      throw result;
    }
  }, Promise.resolve());
};

/** 포트폴리오 담당자 삭제 */
export const assigneeDelete = async (
  groupId: string,
  portfolioId: string,
  assigneeIdList: number[]
) => {
  return await assigneeIdList.reduce(async (promise, id) => {
    await promise;
    const result = await remove(
      `/fund/${groupId}/portfolio/${portfolioId}/assignee/${id}`
    );
    if (result?.isError) {
      throw result;
    }
  }, Promise.resolve());
};

/** 포트폴리오 투자정보 등록 */
export const investCreate = async (
  groupId: string,
  portfolioId: string,
  investList: InvestInfosTypes[]
) => {
  return await investList.reduce(async (promise, list) => {
    await promise;
    await createThrowError(
      `/fund/${groupId}/portfolio/${portfolioId}/invest`,
      list
    );
  }, Promise.resolve());
};

/** 포트폴리오 투자정보 수정 */
export const investUpdate = async (
  groupId: string,
  portfolioId: string,
  investList: InvestInfosTypes[]
) => {
  return await investList.reduce(async (promise, list) => {
    await promise;
    const result = await update(
      `/fund/${groupId}/portfolio/${portfolioId}/invest/${list?.idx}`,
      list
    );
    if (result?.isError) {
      throw result;
    }
  }, Promise.resolve());
};

/** 포트폴리오 투자정보 삭제 */
export const investDelete = async (
  groupId: string,
  portfolioId: string,
  investIdList: number[]
) => {
  return await investIdList.reduce(async (promise, id) => {
    await promise;
    const result = await remove(
      `/fund/${groupId}/portfolio/${portfolioId}/invest/${id}`
    );
    if (result?.isError) {
      throw result;
    }
  }, Promise.resolve());
};

/** 포트폴리오 등록 통합 (담당자 + 투자정보) */
export const portfolioCreate = async ({
  groupId,
  portfolioId,
  assigneeList,
  investList,
}: {
  groupId: string;
  portfolioId: string;
  assigneeList: { email: string; name?: string; phone?: string }[];
  investList: InvestInfosTypes[];
}) => {
  const promise1 = assigneeCreate(groupId, portfolioId, assigneeList);
  const promise2 = investCreate(groupId, portfolioId, investList);
  const promises = [promise1, promise2];

  const result = await Promise.all(promises)
    .then((res) => {
      toast.success(CREATE_SUCCESS_MESSAGE);
      return res;
    })
    .catch((error) => {
      console.log(error);
      toast.error(CREATE_ERROR_MESSAGE);
      return error;
    });

  return result;
};

/** 포트폴리오 수정 통합 (담당자 + 투자정보 수정/삭제) */
export const portfolipUpdate = async ({
  assigneeUpdateList,
  assigneeCreateList,
  assigneeIdList,
  investUpdateList,
  investCreateList,
  investIdList,
  groupId,
  portfolioId,
}: {
  assigneeUpdateList: {
    idx?: number;
    email: string;
    name?: string;
    phone?: string;
  }[];
  assigneeCreateList: { email: string; name?: string; phone?: string }[];
  assigneeIdList: number[];
  investUpdateList: InvestInfosTypes[];
  investCreateList: InvestInfosTypes[];
  investIdList: number[];
  groupId: string;
  portfolioId: string;
}) => {
  /** 담당자 삭제 */
  const promise1 = assigneeDelete(groupId, portfolioId, assigneeIdList);
  /** 담당자 수정 */
  const promise2 = assingneeUpdate(groupId, portfolioId, assigneeUpdateList);
  /** 담당자 추가 */
  const promise3 = assigneeCreate(groupId, portfolioId, assigneeCreateList);
  /** 투자정보 삭제 */
  const promise4 = investDelete(groupId, portfolioId, investIdList);
  /** 투자정보 수정 */
  const promise5 = investUpdate(groupId, portfolioId, investUpdateList);
  /** 투자정보 추가 */
  const promise6 = investCreate(groupId, portfolioId, investCreateList);

  /** 통합 - 각 작업에 이름 부여 */
  const promisesWithName = [
    { name: '담당자 삭제', promise: promise1 },
    { name: '담당자 수정', promise: promise2 },
    { name: '담당자 추가', promise: promise3 },
    { name: '투자정보 삭제', promise: promise4 },
    { name: '투자정보 수정', promise: promise5 },
    { name: '투자정보 추가', promise: promise6 },
  ];

  const result = await Promise.allSettled(
    promisesWithName.map((p) => p.promise)
  );

  // 결과 분석
  const errors: { name: string; message: string }[] = [];
  const successes: string[] = [];

  result.forEach((r, idx) => {
    const taskName = promisesWithName[idx].name;
    if (r.status === 'rejected') {
      // rejected된 경우
      const errorMessage =
        (r.reason as any)?.data?.message ||
        (r.reason as any)?.message ||
        '알 수 없는 오류';
      errors.push({ name: taskName, message: errorMessage });
    } else if ((r.value as any)?.isError === true) {
      // fulfilled이지만 isError가 있는 경우
      const errorMessage =
        (r.value as any)?.data?.message ||
        (r.value as any)?.message ||
        '알 수 없는 오류';
      errors.push({ name: taskName, message: errorMessage });
    } else {
      // 성공
      successes.push(taskName);
    }
  });

  return {
    isError: errors.length > 0,
    hasPartialSuccess: successes.length > 0 && errors.length > 0,
    rejectedCount: errors.length,
    fulfilledCount: successes.length,
    errors: errors,
    successes: successes,
  };
};

/** 포트폴리오 간단조회 */
export function usePortfolioSimpleList() {
  const { groupId } = useParams();
  const { data, isLoading } = useQuery(['portfolio-simple-list', groupId], () =>
    getList(`/fund/${groupId}/portfolio?simple=`, '')
  );

  return {
    simpleListData: data?.map((d: { id: number; name: string }) => ({
      value: d.id,
      label: d.name,
    })),
    isSimpleListLoading: isLoading,
  };
}

/** 포트폴리오 > 상세 > 기본정보 > 기업정보 */
export function usePortfolioDetailByMappingId(mappingId: string) {
  const { data, isLoading } = useQuery(
    ['portfolio-detail-mappingId', mappingId],
    () => getList(`/data-ref/corporation?mappingId=${mappingId}`, ''),
    {
      suspense: true,
      enabled: isNotNil(mappingId),
    }
  );

  return {
    data: data?.content?.[0] || [],
    isLoading,
  };
}

/** 포트폴리오 상세 > 투자자산관리 > 투자자산 목록 조회 */
export function usePortfolioInvestAssetList() {
  const { groupId, portfolioId } = useParams();
  const { pageNo, pageSize } = useQueryString();

  const { data, isLoading } = useQuery(
    ['portfolio-invest-asset-list', groupId, portfolioId],
    () =>
      getList(`/fund/${groupId}/portfolio/${portfolioId}/investment-asset`, ''),
    {
      suspense: true,
    }
  );

  const tableData = useMemo(() => {
    return data?.map((d: InvestAssetAPIResponseTypes, i: number) => ({
      ...d,
      no: data?.length - (pageNo - 1) * pageSize - i,
      id: d?.id,
      date: d?.date,
      type: find(TRANSACTION_TYPES, { value: d?.type })?.label || '-',
      amount:
        d?.amount === 0 ? '비공개' : d?.amount ? comma(d?.amount) + '원' : '-',
      balanceAfter:
        d?.balanceAfter === 0
          ? '비공개'
          : d?.balanceAfter
            ? comma(d?.balanceAfter) + '원'
            : '-',
      profitLoss: d?.profitLoss ? comma(d?.profitLoss) + '원' : '-',
      shares:
        d?.shares === 0 ? '비공개' : d?.shares ? comma(d?.shares) + '주' : '-',
      sharesHeldAfter:
        d?.sharesHeldAfter === 0
          ? '비공개'
          : d?.sharesHeldAfter
            ? comma(d?.sharesHeldAfter) + '주'
            : '-',
      unitPrice:
        d?.unitPrice === 0
          ? '비공개'
          : d?.unitPrice
            ? comma(d?.unitPrice) + '원'
            : '-',
      parValue:
        d?.parValue === 0
          ? '비공개'
          : d?.parValue
            ? comma(d?.parValue) + '원'
            : '-',
      totalIssuedSharesBefore:
        d?.totalIssuedSharesBefore === 0
          ? '비공개'
          : d?.totalIssuedSharesBefore
            ? comma(d?.totalIssuedSharesBefore) + '주'
            : '-',
      totalIssuedSharesAfter:
        d?.totalIssuedSharesAfter === 0
          ? '비공개'
          : d?.totalIssuedSharesAfter
            ? comma(d?.totalIssuedSharesAfter) + '주'
            : '-',
      ownershipRatio:
        d?.sharesHeldAfter && d?.totalIssuedSharesAfter
          ? (
              (Number(d.sharesHeldAfter) / Number(d.totalIssuedSharesAfter)) *
              100
            ).toFixed(2) + '%'
          : '-',
      enterpriseValue:
        d?.enterpriseValue === 0
          ? '비공개'
          : d?.enterpriseValue
            ? comma(d?.enterpriseValue) + '원'
            : '-',

      action: <InvestAssetDeleteButton id={d?.id} />,
    }));
  }, [data]);

  return {
    tableData: tableData || [],
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 포트폴리오 상세 > 투자자산관리 > 투자자산 삭제 */
export function useInvestAssetDelete() {
  const { groupId, portfolioId } = useParams();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    ['invest-asset-delete', groupId, portfolioId],
    (investAssetId: number) => {
      return remove(
        `/fund/${groupId}/portfolio/${portfolioId}/investment-asset/${investAssetId}`
      );
    },
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);

          queryClient.invalidateQueries([
            'portfolio-invest-asset-list',
            groupId,
            portfolioId,
          ]);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 투자자산관리 > 투자자산 등록 */
export function useInvestAssetCreate() {
  const { groupId, portfolioId } = useParams();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    ['invest-asset-create', groupId, portfolioId],
    (params: InvestAssetApiRequestInputs) => {
      return create(
        `/fund/${groupId}/portfolio/${portfolioId}/investment-asset`,
        params
      );
    },
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'portfolio-invest-asset-list',
            groupId,
            portfolioId,
          ]);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 투자자산관리 > 투자자산 단건 조회 */
export function useInvestAssetDetail(assetId: string | null) {
  const { groupId, portfolioId } = useParams();
  const { data, isLoading } = useQuery(
    ['invest-asset-detail', groupId, portfolioId, assetId],
    () =>
      getOne(
        `/fund/${groupId}/portfolio/${portfolioId}/investment-asset`,
        assetId ?? ''
      ),
    {
      enabled: !!assetId,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 포트폴리오 상세 > 투자자산관리 > 투자자산 수정 */
export function useInvestAssetUpdate(
  assetId: string,
  onSuccess: () => void
) {
  const { groupId, portfolioId } = useParams();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ['invest-asset-update', groupId, portfolioId, assetId],
    (value: InvestAssetApiRequestInputs) =>
      update(
        `/fund/${groupId}/portfolio/${portfolioId}/investment-asset/${assetId}`,
        value
      ),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(UPDATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
        } else {
          toast.success(UPDATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'portfolio-invest-asset-list',
            groupId,
            portfolioId,
          ]);
          queryClient.invalidateQueries([
            'invest-asset-detail',
            groupId,
            portfolioId,
            assetId,
          ]);
          onSuccess();
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 사후관리 > 목록 조회 */
export function usePostManagementList() {
  const { groupId, portfolioId } = useParams();
  const { pageNo, pageSize } = useQueryString();

  const params = {
    page: pageNo,
    size: pageSize,
  };

  const { data, isLoading } = useQuery(
    ['post-management-list', groupId, portfolioId, params],
    () =>
      getList(
        `/fund/${groupId}/portfolio/${portfolioId}/post-management`,
        params
      ),
    {
      suspense: true,
    }
  );

  const tableData = useMemo(() => {
    return data?.map(
      (
        d: {
          id: number;
          date: string;
          type: string;
          title: string;

          urls: { title: string; url: string }[];
        },
        i: number
      ) => ({
        ...d,
        no: data?.length - (pageNo - 1) * pageSize - i,
        id: d?.id,
        date: d?.date,
        type: find(POST_MANAGEMENT_TYPES, { value: d?.type })?.label || '-',
        typeValue: d?.type, // 필터링을 위한 원본 type 값
        title: d?.title,
        urls:
          d?.urls?.length > 0 ? (
            <ul css={urlsList}>
              {d?.urls?.map((url: { title: string; url: string }) => (
                <li key={url?.url}>
                  <a href={url?.url} target="_blank" rel="noopener noreferrer">
                    {url?.title}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <span>-</span>
          ),
      })
    );
  }, [data, pageNo, pageSize]);

  return {
    tableData: tableData || [],
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 포트폴리오 상세 > 사후관리 > 등록하기 */
export function usePostManagementCreate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId, portfolioId } = useParams();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    ['post-management-create', groupId, portfolioId],
    (data: FormData | PostManagementFormInputs) => {
      if (data instanceof FormData) {
        return createWithFormData(
          `/fund/${groupId}/portfolio/${portfolioId}/post-management`,
          data
        );
      } else {
        return create(
          `/fund/${groupId}/portfolio/${portfolioId}/post-management`,
          data
        );
      }
    },
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'post-management-list',
            groupId,
            portfolioId,
          ]);
          router.replace(
            `/group/${groupId}/portfolio/${portfolioId}/post-management`
          );
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

const urlsList = css`
  a {
    color: ${palette.colorBlue500};
  }

  li:hover {
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 3px;
    color: ${palette.colorBlue500};
    cursor: pointer;
  }
`;

/** 포트폴리오 상세 > 사후관리 > 상세보기 */
export function usePostManagementDetail() {
  const { groupId, portfolioId, postManagementId } = useParams();

  const { data, isLoading } = useQuery(
    ['post-management-detail', groupId, portfolioId, postManagementId],
    () =>
      getOne(
        `/fund/${groupId}/portfolio/${portfolioId}/post-management`,
        postManagementId
      ),
    {
      suspense: true,
      enabled: !!groupId && !!portfolioId && !!postManagementId,
    }
  );

  return {
    data,
    isLoading,
  };
}

/** 포트폴리오 상세 > 사후관리 > 수정 (JSON PUT, 파일 제외) */
export function usePostManagementUpdate(
  postManagementId: string,
  onSuccess: () => void
) {
  const { groupId, portfolioId } = useParams();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ['post-management-update', groupId, portfolioId, postManagementId],
    (value: PostManagementUpdatePayload) =>
      update(
        `/fund/${groupId}/portfolio/${portfolioId}/post-management/${postManagementId}`,
        value
      ),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(UPDATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
        } else {
          toast.success(UPDATE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'post-management-list',
            groupId,
            portfolioId,
          ]);
          queryClient.invalidateQueries([
            'post-management-detail',
            groupId,
            portfolioId,
            postManagementId,
          ]);
          onSuccess();
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 사후관리 > 삭제 */
export function usePostManagementDelete(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const { groupId, portfolioId, postManagementId } = useParams();
  const queryClient = useQueryClient();
  const router = useInternalRouter();

  const { mutate, isLoading } = useMutation(
    ['post-management-delete', groupId, portfolioId, postManagementId],
    () =>
      remove(
        `/fund/${groupId}/portfolio/${portfolioId}/post-management/${postManagementId}`
      ),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);
          queryClient.invalidateQueries([
            'post-management-list',
            groupId,
            portfolioId,
          ]);
          router.replace(
            `/group/${groupId}/portfolio/${portfolioId}/post-management`
          );
        }
        setConfirmModalOpen(false);
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 포트폴리오 상세 > 보도자료 목록 조회 */
export function usePressList() {
  const { groupId, portfolioId } = useParams();
  const { pageNo, pageSize, pressType } = useQueryString();

  const params: any = {
    page: pageNo,
    size: pageSize,
  };

  if (pressType) {
    params.pressType = pressType;
  }

  const { data, isLoading } = useQuery(
    ['press-list', groupId, portfolioId, params],
    () => getList(`/fund/${groupId}/portfolio/${portfolioId}/press`, params),
    {
      suspense: true,
    }
  );

  const tableData = useMemo(() => {
    return data?.content?.map((d: any, i: number) => ({
      ...d,
      uniqueId: d?.id,
      id: d?.url, // handleDetailPage을 위해서 id 대신 url 값을 사용
      no: data?.totalCount - (pageNo - 1) * pageSize - i,
      type: find(PRESS_TYPES, { value: d?.type })?.label || '-',
      typeValue: d?.type,
      title: d?.title,
      pressMedia: (
        <span
          css={{
            display: 'block',
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {d?.pressMedia || '-'}
        </span>
      ),
      pressAt: d?.pressAt || '-',
    }));
  }, [data, pageNo, pageSize]);

  return {
    data,
    tableData: tableData || [],
    isLoading,
    totalCount: data?.totalCount,
  };
}
